import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types'

import { API } from 'aws-amplify'

import { createR0003Projection, updateR0003Projection } from 'graphql/mutations'
import { listAreas2, getR0003ProjectionData } from 'graphql/queries'

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";

// RadixDash components
import Progress from 'components/Progress/Progress'
import R0003ProjectionSettings from "../R0003ProjectionSettings";
import ProjectionBody from './ProjectionBody'
import ProjectionHeader from './ProjectionHeader';
import { useUser } from 'components/RDAuthContext'
import { useNotes } from '../Report/hooks/useNotes'

import { calculateProjectedCheckCount, cleanProjectionData, ensureProjectedMetricsAreDerived, getForecastedSales, getHistoricalSalesData, initialData, initialTheoHours, normalizeToTwoDecimals, parseFormattedNumber, removeDayKeys, weeks /*, writeToDynamoDB */ } from "./projectionUtils";
import { getAvgCheckHighThreshold, applyAvgCheckAdjustment } from 'services/thresholds/thresholdService';
import { getTheoHoursConfig } from '../Report/utils/mergeMetrics/constants';
import logger from 'utils/logger';
// import { v4 as uuidv4 } from 'uuid'

export default function R0003Projections(props){
  
  const { user: cognitoUser } = useUser()
  const user = cognitoUser.userName
  const role = cognitoUser.role

  const [dataLoading, setDataLoading] = useState(false)
  const [openSettings, setOpenSettings] = useState(true)
  const [newProjection, setNewProjection] = useState(false)
  const [storeConfig, setStoreConfig] = useState([{}])
  const [week, setWeek] = useState(null)
  const [store, setStore] = useState(null)
  const [fohBaseTheoHrs, setFohBaseTheoHrs] = useState(initialTheoHours)
  const [bohBaseTheoHrs, setBohBaseTheoHrs] = useState(initialTheoHours)
  const [data, setData] = useState(initialData)

  const [stores, setStores] = useState(['Fetching stores...'])

  async function setStoreDropdown(){
    try {
      const queryResponse = await API.graphql(
        {
          query: listAreas2,
          variables: {
            report: 'r0003_store_dropdown',
            user: user,
            role: role
          },
          authMode: 'AMAZON_COGNITO_USER_POOLS'
        }
      )

      const results = queryResponse.data.listAreas2[0].stores
      console.log('results - storeConfig', results)
      setStoreConfig(results) 
      const storeArr = results.map(x => x.shortName)
      setStores(storeArr)
    } catch (err) {
      console.log('Error fetching store dropdown data', err)
      setStores(['Error: Unable to Load Stores'])
    }
  }

  useEffect(() => {
    setStoreDropdown()
  }, [])    

  function handleCancel(){
    setOpenSettings(false)
    props.setPage('actions')
  }

  async function handleSelection(selection){
    const selectedWeek = weeks[selection.week]
    const selectedStore = stores[selection.store]
    setWeek(selectedWeek)
    setStore(selectedStore)   
    setOpenSettings(false)
    setDataLoading(true)

    let salesData = {}
    try {
      salesData = await getHistoricalSalesData(selectedStore, selectedWeek)
    } catch (err) {
      console.log('Error getting historical sales data', err)
    }
    console.log('salesData', salesData)

    let forecastedData = {}
    try {
      forecastedData = await getForecastedSales(selectedStore, selectedWeek)
    } catch (err) {
      console.log('Error getting forecasted sales data', err)
    }
    console.log('forecastedData', forecastedData)

    
    // Set Base Theo Hours and get storeId
    const storeIdx = storeConfig.findIndex(x => x.shortName === selectedStore)
    const foh = storeConfig[storeIdx].fohBaseTheoHrs
    const boh = storeConfig[storeIdx].bohBaseTheoHrs

    // Find store info from storeMap by matching shortName
    // storeMap is keyed by longName, so we need to iterate to find matching shortName
    let storeInfo = null
    let storeLongName = null
    let storeRecordId = null

    if (props.storeMap) {
      for (const [longName, store] of props.storeMap.entries()) {
        if (store.shortName === selectedStore) {
          storeInfo = store
          storeLongName = longName
          storeRecordId = store.storeRecordId
          break
        }
      }
    }

    // Also try storeIdMap if we found a storeRecordId
    let storeInfoFromIdMap = null
    if (storeRecordId && props.storeIdMap) {
      storeInfoFromIdMap = props.storeIdMap.get(storeRecordId)
    }

    const storeId = storeRecordId || storeInfo?.id
    const finalStoreInfo = storeInfoFromIdMap || storeInfo

    setFohBaseTheoHrs(foh)
    setBohBaseTheoHrs(boh)    

    // Query the DB to determine if data already exists
    try {
      const result = await API.graphql({
        query: getR0003ProjectionData,
        variables: { week: selectedWeek, store: selectedStore } ,
        authMode: 'AMAZON_COGNITO_USER_POOLS'
      })
      console.log('Query Result', result.data.getR0003ProjectionData)

      if(result.data.getR0003ProjectionData.store == null){
        console.log('New Projection')
        setNewProjection(true)
        setData({
          ...data,
          ...salesData,
          ...forecastedData,
          week: selectedWeek,
          store: selectedStore,
          storeLongName: storeLongName,
          storeRecordId: storeRecordId,
          storeId: storeId
        })
        
      } else {
        console.log('Existing Projection')
        setNewProjection(false)
        let tempObj = JSON.parse(JSON.stringify(result.data.getR0003ProjectionData))
        tempObj.avgWage = tempObj.avgWage.toFixed(2)

        const keyToRemove = '__typename'
        if (tempObj.hasOwnProperty(keyToRemove)) {
          delete tempObj[keyToRemove]
        } 

        const finalData = ensureProjectedMetricsAreDerived({
          ...tempObj,
          ...salesData,
          ...forecastedData,
          storeLongName: storeLongName,
          storeRecordId: storeRecordId,
          storeId: storeId
        })

        setData(finalData)
        
      }
    } catch(err) {
      console.log('Query Error', err)
      console.log('New Projection')
      setNewProjection(true)
      setData({
        ...data,
        ...salesData,
        ...forecastedData,
        week: selectedWeek,
        store: selectedStore,
        storeLongName: storeLongName,
        storeRecordId: storeRecordId,
        storeId: storeId
      })
      
    }  
  }

  useEffect(() => {
    if(data.week !== ''){
      setDataLoading(false)
    }
  }, [data])

  async function handleSubmitProjections(inValues){

    let result

    // Remove the average and past years data

    const projectionValues = removeDayKeys(inValues)

    const values = cleanProjectionData(projectionValues)

    const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

    // Guard: if any day's OneYearAgoAvgCheck is undefined/null in state, the LY
    // data fetch in handleSelection never populated it (silent fetch failure).
    // Saving would write "0.00" to projAvgCheck via the `|| 0` fallback below,
    // silently corrupting the record. A value of 0 / "0.00" is legitimate
    // (store was closed on the corresponding LY day) and is allowed through.
    const missingLyDays = days.filter(day => {
      const v = data[`${day}OneYearAgoAvgCheck`]
      return v === undefined || v === null
    })
    if (missingLyDays.length > 0) {
      logger.warn('R0003 projection save blocked: missing LY avg check in state', {
        week: inValues.week,
        store: inValues.store,
        missingDays: missingLyDays,
      })
      alert(
        `Cannot save projections for ${inValues.store}: last year data failed to load for ${missingLyDays.join(', ')}. Please reload the page and try again.`
      )
      return
    }

    days.forEach(day => {
      const salesKey = `${day}ProjSales`
      const avgCheckKey = `${day}ProjAvgCheck`
      const lyAvgCheckKey = `${day}OneYearAgoAvgCheck`

      values[salesKey] = normalizeToTwoDecimals(values[salesKey])

      // CRITICAL: Save unadjusted OneYearAgoAvgCheck value
      // The displayed value is adjusted, but we ALWAYS save the original
      const unadjustedLyAvgCheck = parseFloat(data[lyAvgCheckKey]) || 0
      values[avgCheckKey] = normalizeToTwoDecimals(unadjustedLyAvgCheck)
    })

    // Remove fields that don't exist in GraphQL schema
    delete values.storeLongName
    delete values.storeRecordId
    delete values.storeId

    console.log('values', values)

    try {
      if(newProjection){
        result = await API.graphql({
          query: createR0003Projection,
          variables: { input: values }
        })
      } else {
        result = await API.graphql({
          query: updateR0003Projection,
          variables: { input: values }
        })
      }
      props.setPage('actions')

    } catch (err) {
      console.log('Create Projection error', err)
    }
  }

  function handleTextFieldChange(event){
    let dataCopy = {...data}
    const field = event.target.name
    const value = event.target.value

    dataCopy[field] = value

    // When projSales changes, update projCheckCount in data
    if (field.endsWith('ProjSales')) {
      const dayPrefix = field.slice(0, 3).toLowerCase()
      const salesKey = `${dayPrefix}ProjSales`
      const checkCountKey = `${dayPrefix}ProjCheckCount`
      const avgCheckKey = `${dayPrefix}ProjAvgCheck`
      const lyAvgCheckKey = `${dayPrefix}OneYearAgoAvgCheck`

      const projSales = parseFormattedNumber(value)
      const lyAvgCheck = parseFormattedNumber(data[lyAvgCheckKey])
      
      const projCheckCount = calculateProjectedCheckCount(projSales, lyAvgCheck)
      console.log('projCheckCount', projCheckCount)

      // const projAvgCheck = parseFloat(projCheckCount) > 0 
      //   ? (projSales / parseFloat(projCheckCount)).toFixed(2) 
      //   : '0.00'
      // console.log('projAvgCheck', projAvgCheck)

      dataCopy[checkCountKey] = projCheckCount
      // dataCopy[avgCheckKey] = projAvgCheck
      dataCopy[avgCheckKey] = lyAvgCheck
    }
    
    setData(dataCopy)
  }

  function getTextFieldValue(day, field){
    const shortDay = `${day.substring(0, 3)}`
    
    switch (field) {
      case 'TheoFOH':
      case 'TheoBOH': {
        // Get store-specific SPLH targets
        const theoConfig = getTheoHoursConfig(store)

        const projSalesKey = `${shortDay.toLowerCase()}ProjSales`
        const baseTheoHours = field === 'TheoFOH' ? fohBaseTheoHrs[shortDay] : bohBaseTheoHrs[shortDay]

        // Determine if weekday (Mon-Thu) or weekend (Fri-Sun)
        const isWeekday = ['Mon', 'Tue', 'Wed', 'Thu'].includes(day)
        let productivityLevel
        if (field === 'TheoFOH') {
          productivityLevel = isWeekday ? theoConfig.foh.weekday : theoConfig.foh.weekend
        } else {
          productivityLevel = isWeekday ? theoConfig.boh.weekday : theoConfig.boh.weekend
        }

        return (data[projSalesKey] / productivityLevel + baseTheoHours).toFixed(2)
      }
      case 'ProjAvgCheck': {
        const lyAvgCheck = parseFloat(data[`${shortDay.toLowerCase()}OneYearAgoAvgCheck`]) || 0

        // Get threshold adjustment for this store - use storeIdMap if we have storeRecordId
        const storeInfo = data.storeRecordId
          ? props.storeIdMap?.get(data.storeRecordId)
          : (data.storeLongName && props.storeMap?.get(data.storeLongName))
        const storeThresholds = storeInfo?.thresholds
        const avgCheckThresholdId = storeThresholds?.avgCheck

        const highThreshold = getAvgCheckHighThreshold(
          data.storeRecordId || data.storeId,
          props.thresholds,
          avgCheckThresholdId
        )

        // Apply adjustment for display
        const adjustedAvgCheck = applyAvgCheckAdjustment(lyAvgCheck, highThreshold)
        return adjustedAvgCheck.toFixed(2)

        // const projSales = parseFloat(data[`${shortDay.toLowerCase()}ProjSales`]) || 0
        // const projCheckCount = parseInt(data[`${shortDay.toLowerCase()}ProjCheckCount`]) || 0
        // return projCheckCount > 0
        //   ? (projSales / projCheckCount).toFixed(2)
        //   : '0.00'
      }
      case 'ProjCheckCount': {
        const sales = data[`${shortDay.toLowerCase()}ProjSales`]
        const avgCheck = data[`${shortDay.toLowerCase()}OneYearAgoAvgCheck`]
        return calculateProjectedCheckCount(sales, avgCheck)
      }

      default:
        return ''
    }   
  }

  return (    
      <MDBox 
        textAlign='center' 
      >
        <R0003ProjectionSettings 
          open={ openSettings }
          cancel={ handleCancel }
          weeks={ weeks }
          stores={ stores }
          close={ handleSelection}
        />        
        
        <ProjectionHeader store={store} week={week}/>

        { dataLoading ?
          <Progress /> :
          <ProjectionBody
            data={data}
            getTextFieldValue={getTextFieldValue}
            handleTextFieldChange={handleTextFieldChange}
            handleSubmitProjections={handleSubmitProjections}
            handleCancel={handleCancel}
            week={week}
            thresholds={props.thresholds}
            storeMap={props.storeMap}
            storeIdMap={props.storeIdMap}
          />
        }      
      </MDBox>
  )
}

R0003Projections.propTypes = {
  setPage: PropTypes.func
}